import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SearchResult } from '@ayumia/contracts';
import { formatRoute, type GraphOptions, type Route } from '../app/router';
import { Icon } from '../design-system/icons';
import { Btn, PageHeader } from '../design-system/primitives';
import { Empty, errorView, Loading } from '../states/States';
import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';
import { RecordDrawer } from '../views/RecordDrawer';
import type { ObjectSummary } from '../views/format';
import { LinkCorrection } from './LinkCorrection';
import { PrepareRecall, TraceTimeline } from './TraceExtras';
import { GraphCanvas, TypeChip, type CanvasCard, type CanvasLane, type CanvasLink } from './GraphCanvas';
import { nodeFacts, relationLabel, relationLabelById, typeLabel, typeVisual, useNodeRecords, useTypeSummaries } from './graph-data';
import { CENTER_W, GROUP_PAGE, layoutGraph, type Layout, type LayoutCard } from './graph-layout';
import {
  CERTAINTY_HINTS,
  COLLAPSE_THRESHOLD,
  DIRECTION_LABELS,
  STROKE_DASH,
  certaintyLabel,
  certaintyStyle,
  countByType,
  directionOf,
  farEnd,
  keyOf,
  pathTo,
  quantityOf,
  relationOfGroup,
  typeOfGroup,
  type WireEdge,
  type WireRef,
  type WireTraversal,
} from './graph-model';
import './graph.css';

/**
 * Explorateur du Business Graph (ADR-129) : lecture seule, sous l'identité courante, sans moteur.
 * - **Voisinage** (`GetNeighborhood`, projection) : un ou deux sauts, fraîcheur affichée.
 * - **Traçabilité** (`TraceUpstream` / `TraceDownstream`, état autoritaire) : profondeur 1 à 10, date d'effet.
 * Un clic ouvre la fiche en tiroir ; un double clic (ou Maj+Entrée) recentre et allonge le fil de l'exploration. Les
 * objets que l'utilisateur ne peut pas lire n'apparaissent pas et ne sont pas comptés (ERP-AUTH-003) ; une
 * limite atteinte est dite (ERP-PROJ-001). Le tableau donne la même information que le schéma.
 */

type GraphRoute = Extract<Route, { kind: 'graph' }>;
type Selection = { kind: 'link'; key: string } | { kind: 'group'; group: string } | null;

const plural = (count: number, one: string, many: string): string => `${count} ${count > 1 ? many : one}`;

/**
 * Natures suivies en traçabilité. Composition et transformation ne suffisent pas : un chasseur, sa réception et ses
 * carcasses sont reliés par la provenance (remarque de l'utilisateur du 2026-10-15 : « Traçabilité est toujours vide »).
 * L'amont rejoint aussi les références, jusqu'au client et au fournisseur (ADR-059) ; l'aval n'en suit pas, pour ne pas
 * ramener tout ce qui cite un objet (règlements d'un tiers…).
 */
const TRACE_KINDS_DOWNSTREAM = ['composition', 'transformation', 'provenance_link'] as const;
const TRACE_KINDS_UPSTREAM = [...TRACE_KINDS_DOWNSTREAM, 'reference'] as const;

/** Étendue en mots plutôt qu'en profondeur chiffrée dans une liste (remarque de l'utilisateur du 2026-10-15). */
const NEIGHBORHOOD_SPANS = [
  { id: '1', label: 'Liens directs' },
  { id: '2', label: 'Liens des liens' },
] as const;
const TRACE_SPANS = [
  { id: '1', label: '1 étape' },
  { id: '2', label: '2 étapes' },
  { id: '4', label: '4 étapes' },
  { id: '10', label: 'Tout' },
] as const;

function spanOptions(mode: 'neighborhood' | 'trace', depth: number): Array<{ id: string; label: string }> {
  const options: Array<{ id: string; label: string }> = [...(mode === 'trace' ? TRACE_SPANS : NEIGHBORHOOD_SPANS)];
  // Une profondeur venue d'un lien partagé reste choisie, à sa place.
  if (!options.some((option) => option.id === String(depth))) options.push({ id: String(depth), label: `${depth} étapes` });
  return options.sort((a, b) => Number(a.id) - Number(b.id));
}

function settingsOf(route: GraphRoute): Required<Pick<GraphOptions, 'mode' | 'depth'>> & GraphOptions {
  const mode = route.mode ?? 'neighborhood';
  return { ...route, mode, depth: route.depth ?? (mode === 'trace' ? 4 : 1), ...(mode === 'trace' ? { direction: route.direction ?? 'downstream' } : {}) };
}

function graphRoute(ref: WireRef, settings: GraphOptions): GraphRoute {
  const route: GraphRoute = { kind: 'graph', object_type: ref.type, record_id: ref.id, mode: settings.mode ?? 'neighborhood' };
  if (settings.depth !== undefined) route.depth = settings.depth;
  if (settings.mode === 'trace') {
    if (settings.direction !== undefined) route.direction = settings.direction;
    if (settings.at !== undefined) route.at = settings.at;
  }
  return route;
}

const dateTime = (iso: string): string => {
  const date = new Date(iso);
  return `${date.toLocaleDateString('fr-FR')} à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

export function GraphPage({ route }: { route: GraphRoute }) {
  if (route.object_type === undefined || route.record_id === undefined) return <GraphStart />;
  return <GraphExplorer route={route} />;
}

/** Départ de l'exploration : la recherche d'un objet (`Search`, relue sous les droits de l'appelant). */
function GraphStart() {
  const services = useAppServices();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [found, reload] = useLoad<SearchResult[] | null>(`graph-search:${submitted}`, async () => (submitted === '' ? null : (await services.client.query<SearchResult[]>('Search', { q: submitted, limit: '20' })).data));
  const resultTypes = useMemo(() => (found.status === 'ready' && found.value !== null ? found.value.map((result) => result.object_type) : []), [found]);
  const resultSummaries = useTypeSummaries(services.client, resultTypes);
  return (
    <section className="ay-view ay-graph" aria-labelledby="ay-graph-title">
      <PageHeader titleId="ay-graph-title" title="Graphe" subtitle="Ce qui est relié à un objet : amont à gauche, aval à droite, références au-dessus et en dessous. Cherchez l’objet de départ." />
      <form
        className="ay-graph__start"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(q.trim());
        }}
      >
        <label className="ay-graph__search">
          <Icon name="search" size={16} />
          <span className="ay-visually-hidden">Rechercher un objet</span>
          <input type="search" value={q} placeholder="Numéro de réception, lot, bracelet, tiers…" aria-describedby="ay-graph-search-help" onChange={(event) => setQ(event.target.value)} />
        </label>
        <Btn type="submit" variant="dark">
          Rechercher
        </Btn>
      </form>
      {/* La recherche lit l'index des champs cherchables (numéros, noms) ; une valeur d'énumération (espèce, statut) n'y est pas (ADR-129, tranche 1). */}
      <p id="ay-graph-search-help" className="ay-graph__muted ay-graph__searchhelp">
        Cherchez par numéro : réception, lot, bracelet, tiers…
      </p>
      {submitted !== '' && found.status === 'loading' && <Loading />}
      {found.status === 'error' && errorView(found.error, reload)}
      {found.status === 'ready' && found.value !== null && found.value.length === 0 && <Empty label="Aucun objet trouvé." />}
      {found.status === 'ready' && found.value !== null && found.value.length > 0 && (
        <ul className="ay-graph__results" aria-label="Objets trouvés">
          {found.value.map((result) => {
            const visual = typeVisual(result.object_type, resultSummaries.get(result.object_type));
            const target = graphRoute({ type: result.object_type, id: String(result.object_id) }, { mode: 'neighborhood' });
            return (
              <li key={`${result.object_type}:${String(result.object_id)}`}>
                <a
                  href={formatRoute(target)}
                  onClick={(event) => {
                    event.preventDefault();
                    services.navigate(target);
                  }}
                >
                  <TypeChip icon={visual.icon} tone={visual.tone} size={26} />
                  <span>
                    <strong>{result.title}</strong>
                    <span className="ay-graph__muted"> · {typeLabel(resultSummaries.get(result.object_type), result.object_type)}</span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

async function loadTraversal(client: ReturnType<typeof useAppServices>['client'], route: GraphRoute, settings: ReturnType<typeof settingsOf>): Promise<WireTraversal> {
  const parameters: Record<string, string> = { object_type: route.object_type!, object_id: route.record_id!, depth: String(settings.depth) };
  if (settings.mode === 'neighborhood') return (await client.query<WireTraversal>('GetNeighborhood', parameters)).data;
  if (settings.at !== undefined) parameters['at'] = settings.at;
  parameters['kinds'] = (settings.direction === 'upstream' ? TRACE_KINDS_UPSTREAM : TRACE_KINDS_DOWNSTREAM).join(',');
  return (await client.query<WireTraversal>(settings.direction === 'upstream' ? 'TraceUpstream' : 'TraceDownstream', parameters)).data;
}

function GraphExplorer({ route }: { route: GraphRoute }) {
  const services = useAppServices();
  const settings = settingsOf(route);
  const centerRef: WireRef = { type: route.object_type!, id: route.record_id! };
  const key = formatRoute(route);
  const [loaded, reload] = useLoad<WireTraversal>(key, () => loadTraversal(services.client, route, settings));
  // Fil de l'exploration : les objets recentrés successivement ; revenir à l'un d'eux coupe la suite.
  const [trail, setTrail] = useState<WireRef[]>([centerRef]);
  useEffect(() => {
    setTrail((current) => {
      const at = current.findIndex((entry) => entry.type === centerRef.type && entry.id === centerRef.id);
      return at >= 0 ? current.slice(0, at + 1) : [...current, centerRef];
    });
    // Le fil suit l'objet exploré.
  }, [centerRef.type, centerRef.id]);
  const go = (ref: WireRef, next: GraphOptions = settings): void => services.navigate(graphRoute(ref, next));
  // La fiche s'ouvre en tiroir par-dessus le graphe : l'exploration reste en place derrière (remarque de l'utilisateur du 2026-10-15).
  const [opened, setOpened] = useState<WireRef | null>(null);
  const open = (ref: WireRef): void => setOpened(ref);

  const summaries = useTypeSummaries(services.client, loaded.status === 'ready' ? loaded.value.nodes.map((node) => node.ref.type) : [centerRef.type]);
  const centerSummary = summaries.get(centerRef.type);
  const centerName = loaded.status === 'ready' ? (loaded.value.nodes.find((node) => keyOf(node.ref) === keyOf(centerRef))?.label ?? null) : null;
  const heading = `${settings.mode === 'trace' ? (settings.direction === 'upstream' ? 'Traçabilité amont' : 'Traçabilité aval') : 'Liens'} ${centerSummary === undefined || centerSummary === null ? '' : `de ${typeLabel(centerSummary, centerRef.type).toLocaleLowerCase('fr')} `}${centerName ?? ''}`.trim();

  // Plein écran : le panneau couvre la fenêtre ; Échap en sort, sauf quand une fiche est ouverte (elle se ferme d'abord).
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return;
    // Le contenu de la page forme un empilement de niveau 1 : il passe au-dessus de la barre et du rail le temps du plein écran.
    document.body.classList.add('ay-graph-fullscreen');
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && opened === null) setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('ay-graph-fullscreen');
    };
  }, [full, opened]);

  const controls = (
    <>
      <Segmented
        label="Mode d’exploration"
        value={settings.mode}
        options={[
          { id: 'neighborhood', label: 'Voisinage' },
          { id: 'trace', label: 'Traçabilité' },
        ]}
        onChange={(mode) => go(centerRef, mode === 'trace' ? { mode: 'trace', depth: 4, direction: 'downstream' } : { mode: 'neighborhood', depth: 1 })}
      />
      {settings.mode === 'trace' && (
        <Segmented
          label="Sens de la traçabilité"
          value={settings.direction ?? 'downstream'}
          options={[
            { id: 'upstream', label: 'Amont' },
            { id: 'downstream', label: 'Aval' },
          ]}
          onChange={(direction) => go(centerRef, { ...settings, direction })}
        />
      )}
      <Segmented label="Étendue" value={String(settings.depth)} options={spanOptions(settings.mode, settings.depth)} onChange={(depth) => go(centerRef, { ...settings, depth: Number(depth) })} />
      {settings.mode === 'trace' && (
        <label className="ay-graph__field">
          Date d’effet
          <input
            type="datetime-local"
            value={settings.at === undefined ? '' : localInput(settings.at)}
            onChange={(event) => {
              const next: GraphOptions = { ...settings };
              if (event.target.value === '') delete next.at;
              else next.at = new Date(event.target.value).toISOString();
              go(centerRef, next);
            }}
          />
        </label>
      )}
      <span className="ay-graph__spacer" />
      <HelpButton trace={settings.mode === 'trace'} />
      <button type="button" className="ay-graph__iconbutton" aria-pressed={full} aria-label={full ? 'Quitter le plein écran' : 'Plein écran'} title={full ? 'Quitter le plein écran (Échap)' : 'Plein écran'} onClick={() => setFull((value) => !value)}>
        <Icon name={full ? 'minimize' : 'maximize'} size={16} />
      </button>
    </>
  );

  return (
    <section className={`ay-view ay-graph ${full ? 'ay-graph--full' : 'ay-graph--page'}`} aria-labelledby="ay-graph-title">
      {full ? (
        <h1 id="ay-graph-title" className="ay-visually-hidden">
          {heading}
        </h1>
      ) : (
        <PageHeader titleId="ay-graph-title" title={heading} />
      )}
      {loaded.status === 'ready' ? (
        <ExplorerBody traversal={loaded.value} summaries={summaries} settings={settings} trail={trail} controls={controls} onRecenter={(ref) => go(ref)} onOpen={open} />
      ) : (
        <section className="ay-card ay-graph__panel" aria-label="Liens">
          <div className="ay-graph__bar">{controls}</div>
          {loaded.status === 'loading' && <Loading label="Lecture des liens…" />}
          {loaded.status === 'error' && errorView(loaded.error, reload)}
        </section>
      )}
      {opened !== null && (
        <RecordDrawer
          key={keyOf(opened)}
          objectType={opened.type}
          recordId={opened.id}
          title={loaded.status === 'ready' ? loaded.value.nodes.find((node) => keyOf(node.ref) === keyOf(opened))?.label : undefined}
          onClose={() => setOpened(null)}
        />
      )}
    </section>
  );
}

/** Aide du graphe derrière une icône : gestes, mode, droits (remarque de l'utilisateur du 2026-10-15, gagner de la place). */
function HelpButton({ trace }: { trace: boolean }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent): void => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <span ref={box} className="ay-graph__helpwrap">
      <button type="button" className="ay-graph__iconbutton" aria-label="Aide du graphe" aria-expanded={open} title="Aide" onClick={() => setOpen((value) => !value)}>
        <Icon name="help" size={16} />
      </button>
      {open && (
        <div className="ay-card ay-graph__helppop" role="note" aria-label="Aide du graphe">
          <p>
            {trace
              ? 'Traçabilité : tout ce qui est relié par composition, transformation ou provenance, lu sur l’état autoritaire. Un rappel ou un périmètre d’impact se lit dans ce mode.'
              : 'Voisinage : ce qui est relié à l’objet, l’amont à gauche, l’aval à droite, les références au-dessus et en dessous. Pour un rappel, passez en mode Traçabilité.'}
          </p>
          <ul>
            <li>Un clic ouvre la fiche ; un double clic (ou Maj+Entrée) recentre l’exploration.</li>
            <li>Molette : faire défiler. Ctrl + molette : zoomer. Glisser le fond : déplacer la vue.</li>
            <li>Un groupe montre {GROUP_PAGE} objets ; « Afficher plus » donne la suite.</li>
            <li>Les objets que vous ne pouvez pas lire n’apparaissent pas et ne sont pas comptés.</li>
          </ul>
        </div>
      )}
    </span>
  );
}

function localInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function SourceBadge({ traversal }: { traversal: WireTraversal }) {
  if (traversal.source === 'authoritative') {
    return (
      <span className="ay-graph__source ay-graph__source--exact">
        <Icon name="check" size={14} />
        Exact, lu sur l’état autoritaire{traversal.at === undefined ? '' : ` au ${dateTime(traversal.at)}`}
      </span>
    );
  }
  return (
    <span className="ay-graph__source">
      <Icon name="clock" size={14} />
      {traversal.as_of === undefined ? 'Fraîcheur du voisinage inconnue' : `Voisinage à jour au ${dateTime(traversal.as_of)}`}
    </span>
  );
}

function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: ReadonlyArray<{ id: T; label: string }>; onChange: (value: T) => void }) {
  return (
    <div className="ay-graph__segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.id} type="button" aria-pressed={option.id === value} onClick={() => option.id !== value && onChange(option.id)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ExplorerBody({
  traversal,
  summaries,
  settings,
  trail,
  controls,
  onRecenter,
  onOpen,
}: {
  traversal: WireTraversal;
  summaries: ReadonlyMap<string, ObjectSummary | null>;
  settings: ReturnType<typeof settingsOf>;
  trail: readonly WireRef[];
  /** Commandes de la page (mode, sens, étendue, aide, plein écran), en tête du panneau. */
  controls: ReactNode;
  onRecenter: (ref: WireRef) => void;
  onOpen: (ref: WireRef) => void;
}) {
  const services = useAppServices();
  const trace = settings.mode === 'trace';
  const startKey = keyOf(traversal.start);
  const [representation, setRepresentation] = useState<'schema' | 'table'>('schema');
  const [hiddenTypes, setHiddenTypes] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Selection>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [legendOpen, setLegendOpen] = useState(true);
  // Objets montrés par groupe : « Afficher plus » en ajoute une page ; un autre objet exploré repart de la première.
  const [shown, setShown] = useState<ReadonlyMap<string, number>>(new Map());
  useEffect(() => setShown(new Map()), [startKey]);
  const layout = useMemo(() => layoutGraph(traversal, { hiddenTypes, trace, shown }), [traversal, hiddenTypes, trace, shown]);
  const counts = useMemo(() => countByType(traversal.nodes, startKey), [traversal, startKey]);
  const visibleRefs = useMemo(() => layout.cards.flatMap((card) => (card.node === undefined ? [] : [card.node.ref])), [layout]);
  const records = useNodeRecords(services.client, visibleRefs);
  const labelOf = (ref: WireRef): string => nodeFacts(summaries.get(ref.type), records.get(keyOf(ref)), layout.placed.get(keyOf(ref))?.label, ref.id, 0).title;
  const nameOf = (ref: WireRef): string => `${typeLabel(summaries.get(ref.type), ref.type)} ${labelOf(ref)}`;

  // Chemin surligné : celui du lien choisi, sinon du nœud survolé ou atteint au clavier.
  const selectedLink = selection?.kind === 'link' ? layout.links.find((link) => link.key === selection.key) : undefined;
  const lit = useMemo(() => {
    if (selectedLink !== undefined) {
      const edge = traversal.edges[selectedLink.edges[0]!]!;
      const far = keyOf(farEnd(layout.placed, edge));
      const path = pathTo(layout.placed, far);
      for (const index of selectedLink.edges) path.edges.add(index);
      for (const card of [selectedLink.from, selectedLink.to]) path.nodes.add(card);
      return path;
    }
    if (focused !== null && layout.placed.has(focused)) return pathTo(layout.placed, focused);
    return null;
  }, [selectedLink, focused, layout, traversal]);
  const term = search.trim().toLocaleLowerCase('fr');
  const matches = (card: LayoutCard): boolean => {
    if (term === '' || card.node === undefined) return false;
    const facts = nodeFacts(summaries.get(card.node.ref.type), records.get(card.key), card.node.label, card.node.ref.id, 0);
    return [facts.title, facts.code ?? '', typeLabel(summaries.get(card.node.ref.type), card.node.ref.type)].some((value) => value.toLocaleLowerCase('fr').includes(term));
  };
  const cardLit = (card: LayoutCard): boolean => (lit !== null && (lit.nodes.has(card.key) || (card.group !== undefined && card.group.members.some((member) => lit.nodes.has(member))))) || matches(card);
  const anyLit = lit !== null || (term !== '' && layout.cards.some(matches));

  const lanes: CanvasLane[] = laneRects(layout, trace, settings.direction, summaries);
  const cards: CanvasCard[] = layout.cards.map((card) => {
    const cardType = card.node?.ref.type ?? typeOfGroup(card.group?.group ?? card.more?.group ?? '');
    const visual = typeVisual(cardType, summaries.get(cardType));
    const on = cardLit(card);
    const base = { key: card.key, x: card.x, y: card.y, w: card.w, h: card.h, sides: card.sides, tone: visual.tone, dimmed: anyLit && !on && card.kind !== 'center', highlighted: on };
    if (card.kind === 'more') {
      const more = card.more!;
      const typeName = typeLabel(summaries.get(typeOfGroup(more.group)), typeOfGroup(more.group), more.remaining > 1).toLocaleLowerCase('fr');
      const next = Math.min(GROUP_PAGE, more.remaining);
      return {
        ...base,
        variant: 'more',
        label: `Afficher ${next} de plus : encore ${more.remaining} ${typeName}`,
        onActivate: () =>
          setShown((current) => {
            const visibleNow = layout.cards.filter((other) => other.node !== undefined && other.node.group === more.group).length;
            return new Map(current).set(more.group, visibleNow + GROUP_PAGE);
          }),
        children: (
          <span className="ay-graph__morebody">
            <Icon name="plus" size={14} />
            <span>
              <strong>Afficher {next} de plus</strong>
              <span className="ay-graph__muted">
                Encore {more.remaining} {typeName}
              </span>
            </span>
          </span>
        ),
      };
    }
    if (card.kind === 'collapsed') {
      const group = card.group!;
      const typeName = typeLabel(summaries.get(typeOfGroup(group.group)), typeOfGroup(group.group), true).toLocaleLowerCase('fr');
      return {
        ...base,
        variant: 'collapsed',
        label: `${group.members.length} ${typeName} repliés : déplier en liste`,
        onActivate: () => setSelection({ kind: 'group', group: group.group }),
        children: (
          <span className="ay-graph__cardbody">
            <TypeChip icon={visual.icon} tone={visual.tone} />
            <span>
              <span className="ay-graph__eyebrow">{relationLabelById(summaries, relationOfGroup(group.group))}</span>
              <strong>
                {group.members.length} {typeName}
              </strong>
              <span className="ay-graph__unfold">Repliés : déplier en liste</span>
            </span>
          </span>
        ),
      };
    }
    const node = card.node!;
    const summary = summaries.get(node.ref.type);
    const facts = nodeFacts(summary, records.get(card.key), node.label, node.ref.id, card.kind === 'center' ? 4 : 1);
    const via = node.via === undefined ? undefined : traversal.edges[node.via];
    const certainty = via === undefined ? undefined : via.certainty;
    const name = `${typeLabel(summary, node.ref.type)} ${facts.title}`;
    return {
      ...base,
      variant: card.kind === 'center' ? 'center' : 'node',
      label: card.kind === 'center' ? `${name} (objet exploré)` : name,
      description: card.kind === 'center' ? 'Entrée ou clic : ouvrir la fiche.' : 'Entrée ou clic : ouvrir la fiche. Maj+Entrée ou double clic : recentrer.',
      // Remarque de l'utilisateur du 2026-10-15 : un clic ouvre la fiche, un double clic change de vue.
      onActivate: () => onOpen(node.ref),
      ...(card.kind === 'center' ? {} : { onDoubleActivate: () => onRecenter(node.ref) }),
      onFocus: () => setFocused(card.key),
      children: (
        <span className="ay-graph__cardbody">
          <TypeChip icon={visual.icon} tone={visual.tone} />
          <span className="ay-graph__cardtext">
            <span className="ay-graph__eyebrow">{typeLabel(summary, node.ref.type)}</span>
            {card.kind === 'center' ? <strong className="ay-graph__centertitle">{facts.title}</strong> : <strong>{facts.title}</strong>}
            {facts.code !== undefined && facts.code !== facts.title && <span className="ay-graph__mono">{facts.code}</span>}
            {facts.status !== undefined && <span className="ay-graph__status">{facts.status.label}</span>}
            {card.kind === 'center' ? (
              <span className="ay-graph__facts">
                {facts.attributes.map((attribute) => (
                  <span key={attribute.label}>
                    <span className="ay-graph__muted">{attribute.label}</span>
                    <b>{attribute.value}</b>
                  </span>
                ))}
                <span>
                  <span className="ay-graph__muted">Liens</span>
                  <span>
                    <span className="ay-graph__mono">{traversal.edges.length}</span> · {plural(settings.depth, trace ? 'niveau' : 'saut', trace ? 'niveaux' : 'sauts')}
                  </span>
                </span>
              </span>
            ) : (
              facts.attributes.map((attribute) => (
                <span key={attribute.label} className="ay-graph__muted">
                  {attribute.value}
                </span>
              ))
            )}
            {certainty !== undefined && certaintyStyle(certainty) !== 'exact' && <span className={`ay-graph__certainty ay-graph__certainty--${certaintyStyle(certainty)}`}>{certaintyLabel(certainty)}</span>}
          </span>
        </span>
      ),
    };
  });
  const links: CanvasLink[] = layout.links.map((link) => {
    const edge = traversal.edges[link.edges[0]!]!;
    const style = certaintyStyle(edge.certainty);
    const on = lit !== null && link.edges.some((index) => lit.edges.has(index));
    const relation = relationLabel(summaries, edge);
    const dash = STROKE_DASH[style];
    return {
      key: link.key,
      d: link.d,
      ...(dash === undefined ? {} : { dash }),
      width: link.width,
      arrow: link.arrow,
      dimmed: anyLit && !on && selection?.kind === 'link' ? selection.key !== link.key : anyLit && !on,
      highlighted: on,
      selected: selection?.kind === 'link' && selection.key === link.key,
      label: { text: relation, x: link.labelX, y: link.labelY, ...(style === 'exact' ? {} : { badge: certaintyLabel(edge.certainty) }) },
      ariaLabel: `Lien « ${relation} » : ${nameOf(edge.source)} vers ${nameOf(edge.target)}, certitude ${certaintyLabel(edge.certainty)}`,
      onSelect: () => setSelection(selection?.kind === 'link' && selection.key === link.key ? null : { kind: 'link', key: link.key }),
    };
  });
  // En-têtes des groupes, dessinés comme des libellés de couloir.
  const headerLanes: CanvasLane[] = layout.headers.map((header) => {
    const type = typeOfGroup(header.group);
    const relation = relationOfGroup(header.group);
    const title = `${relation === '-' ? '' : `${relationLabelById(summaries, relation)} · `}${typeLabel(summaries.get(type), type, header.count > 1)}`;
    return { key: header.key, x: header.x, y: header.y, w: header.w, h: 0, title: `${title} · ${header.count}`, variant: 'header' };
  });

  const types = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const listRoute = (type: string): Route => listOf(type, traversal.start, summaries.get(type));
  const dominant = types[0]?.[0];
  const quantity = trace ? totalQuantity(traversal.edges) : null;

  return (
    <>
      {trace && (
        <div className="ay-graph__kpis" aria-label="Périmètre">
          {types.map(([type, count]) => (
            <div key={type} className="ay-graph__kpi">
              <span className="ay-graph__kpivalue">{count}</span>
              <span>{typeLabel(summaries.get(type), type, count > 1).toLocaleLowerCase('fr')}</span>
            </div>
          ))}
          {quantity !== null && (
            <div className="ay-graph__kpi">
              <span className="ay-graph__kpivalue">
                {quantity.value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} {quantity.unit}
              </span>
              <span>concernés (quantités portées par les liens)</span>
            </div>
          )}
          {types.length === 0 && <div className="ay-graph__kpi">Aucun objet relié dans ce sens.</div>}
          <div className="ay-graph__kpiaction">
            <PrepareRecall start={traversal.start} />
          </div>
        </div>
      )}
      {trace && <TraceTimeline traversal={traversal} summaries={summaries} />}
      {traversal.truncated && (
        <div className="ay-banner ay-banner--danger ay-graph__limit" role="alert">
          <span>
            {trace
              ? 'Limite atteinte : la traversée s’est arrêtée avant la fin (1 000 objets ou profondeur choisie). Le périmètre affiché est incomplet ; ne l’utilisez pas tel quel pour un rappel.'
              : 'Limite atteinte : un objet a plus de 500 liens ; le voisinage affiché est incomplet.'}
          </span>
          {dominant !== undefined && (
            <Btn variant="soft" onClick={() => services.navigate(listRoute(dominant))}>
              Voir en liste
            </Btn>
          )}
          {trace && settings.depth > 1 && <Btn onClick={() => onRecenter(traversal.start)}>Recharger</Btn>}
        </div>
      )}
      <section className="ay-card ay-graph__panel" aria-label={`Liens de ${nameOf(traversal.start)}`}>
        <div className="ay-graph__bar">{controls}</div>
        <div className="ay-graph__bar">
          <nav aria-label="Exploration" className="ay-graph__trail">
            {trail.map((ref, index) =>
              index === trail.length - 1 ? (
                <strong key={keyOf(ref)} aria-current="page">
                  {nameOf(ref)}
                </strong>
              ) : (
                <span key={keyOf(ref)}>
                  <a
                    href={formatRoute(graphRoute(ref, settings))}
                    onClick={(event) => {
                      event.preventDefault();
                      onRecenter(ref);
                    }}
                  >
                    {nameOf(ref)}
                  </a>
                  <span aria-hidden="true"> › </span>
                </span>
              ),
            )}
          </nav>
          <SourceBadge traversal={traversal} />
          <span className="ay-graph__spacer" />
          <label className="ay-graph__search">
            <Icon name="search" size={15} />
            <span className="ay-visually-hidden">Rechercher dans le graphe</span>
            <input type="search" placeholder="Rechercher dans le graphe" value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <Segmented
            label="Représentation"
            value={representation}
            options={[
              { id: 'schema', label: 'Schéma' },
              { id: 'table', label: 'Tableau' },
            ]}
            onChange={setRepresentation}
          />
        </div>
        {types.length > 0 && (
          <div className="ay-graph__filters" role="group" aria-label="Types affichés">
            <span className="ay-graph__muted">Types :</span>
            {types.map(([type, count]) => {
              const visual = typeVisual(type, summaries.get(type));
              const shown = !hiddenTypes.has(type);
              return (
                <button
                  key={type}
                  type="button"
                  aria-pressed={shown}
                  className="ay-graph__filter"
                  onClick={() =>
                    setHiddenTypes((current) => {
                      const next = new Set(current);
                      if (shown) next.add(type);
                      else next.delete(type);
                      return next;
                    })
                  }
                >
                  <span className={`ay-graph__dot ay-graph__tone--t${visual.tone}`} aria-hidden="true" />
                  {typeLabel(summaries.get(type), type)}
                  <span className="ay-graph__mono">{count}</span>
                </button>
              );
            })}
            {layout.collapsed.size > 0 && (
              <span className="ay-graph__note" role="status">
                Plus de {COLLAPSE_THRESHOLD} objets visibles : {plural(layout.collapsed.size, 'groupe replié', 'groupes repliés')}, chacun se déplie en liste.
              </span>
            )}
          </div>
        )}
        {representation === 'schema' ? (
          <div className="ay-graph__stage">
            {traversal.edges.length === 0 ? (
              <Empty label={trace ? 'Aucun objet relié dans ce sens, à cette profondeur.' : 'Aucun lien lisible autour de cet objet.'} />
            ) : (
              <GraphCanvas
                width={layout.width}
                height={layout.height}
                lanes={[...lanes, ...headerLanes]}
                cards={cards}
                links={links}
                label={`Schéma des liens de ${nameOf(traversal.start)}`}
                overlay={<Legend open={legendOpen} onToggle={() => setLegendOpen((value) => !value)} types={types.map(([type]) => type)} summaries={summaries} />}
              />
            )}
            <SidePanel
              selection={selection}
              layout={layout}
              traversal={traversal}
              summaries={summaries}
              labelOf={labelOf}
              nameOf={nameOf}
              onClose={() => setSelection(null)}
              onRecenter={onRecenter}
              onOpen={onOpen}
              listRoute={listRoute}
            />
          </div>
        ) : (
          <LinkTable traversal={traversal} layout={layout} summaries={summaries} nameOf={nameOf} onRecenter={onRecenter} onOpen={onOpen} />
        )}
      </section>
    </>
  );
}

/** Couloirs Amont, Objet exploré, Aval (voisinage) ; en traçabilité, un couloir par niveau. */
function laneRects(layout: Layout, trace: boolean, direction: GraphOptions['direction'], summaries: ReadonlyMap<string, ObjectSummary | null>): CanvasLane[] {
  const height = layout.height - 16;
  if (trace) {
    return layout.columns.map((column) => {
      const types = [...new Set(layout.cards.filter((card) => card.column === column.column).map((card) => card.node?.ref.type ?? typeOfGroup(card.group?.group ?? card.more?.group ?? '')))];
      const title = column.column === 0 ? 'Objet de départ' : types.map((type) => typeLabel(summaries.get(type), type, true)).join(', ');
      return { key: `lane:${column.column}`, x: column.x - 8, y: 8, w: column.w + 16, h: height, title, count: column.column === 0 ? undefined : `niveau ${Math.abs(column.column)} ${direction === 'upstream' ? 'amont' : 'aval'}`, ...(column.column === 0 ? { variant: 'accent' as const } : {}) } as CanvasLane;
    });
  }
  const span = (filter: (column: number) => boolean): { x: number; w: number } | null => {
    const members = layout.columns.filter((column) => filter(column.column));
    if (members.length === 0) return null;
    const first = members[0]!;
    const last = members[members.length - 1]!;
    return { x: first.x - 8, w: last.x + last.w - first.x + 16 };
  };
  const count = (filter: (column: number) => boolean): number => layout.cards.filter((card) => filter(card.column) && card.kind !== 'center').reduce((sum, card) => sum + (card.more?.remaining ?? card.group?.members.length ?? 1), 0);
  const lanes: CanvasLane[] = [];
  const up = span((column) => column < 0);
  const center = span((column) => column === 0);
  const down = span((column) => column > 0);
  if (up !== null) lanes.push({ key: 'lane:up', x: up.x, y: 8, w: up.w, h: height, title: 'Amont', count: plural(count((column) => column < 0), 'objet', 'objets') });
  if (center !== null) lanes.push({ key: 'lane:center', x: center.x, y: 8, w: Math.max(center.w, CENTER_W), h: height, title: 'Objet exploré', variant: 'accent' });
  if (down !== null) lanes.push({ key: 'lane:down', x: down.x, y: 8, w: down.w, h: height, title: 'Aval', count: plural(count((column) => column > 0), 'objet', 'objets') });
  return lanes;
}

function totalQuantity(edges: readonly WireEdge[]): { value: number; unit: string } | null {
  const byUnit = new Map<string, number>();
  for (const edge of edges) {
    const quantity = quantityOf(edge);
    if (quantity !== null) byUnit.set(quantity.unit ?? '', (byUnit.get(quantity.unit ?? '') ?? 0) + quantity.value);
  }
  if (byUnit.size !== 1) return null;
  const [unit, value] = [...byUnit.entries()][0]!;
  return { value, unit };
}

/**
 * Liste filtrée d'un type, quand il porte une seule référence filtrable vers le type exploré (la même règle
 * que l'onglet d'une vue intégrée) ; sinon la liste du type, sans filtre deviné.
 */
function listOf(type: string, start: WireRef, summary: ObjectSummary | null | undefined): Route {
  const links = (summary?.fields ?? []).filter((field) => field.reference_type === start.type && field.filterable === true);
  return links.length === 1 ? { kind: 'object', object_type: type, list: `f.${links[0]!.field_id}.eq=${start.id}` } : { kind: 'object', object_type: type };
}

function Legend({ open, onToggle, types, summaries }: { open: boolean; onToggle: () => void; types: readonly string[]; summaries: ReadonlyMap<string, ObjectSummary | null> }) {
  return (
    <div className="ay-graph__legend">
      <button type="button" aria-expanded={open} onClick={onToggle}>
        Légende <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <>
          <div className="ay-graph__legendrow">
            {types.map((type) => (
              <span key={type}>
                <span className={`ay-graph__dot ay-graph__tone--t${typeVisual(type).tone}`} aria-hidden="true" />
                {typeLabel(summaries.get(type), type)}
              </span>
            ))}
          </div>
          <div className="ay-graph__legendrow">
            {(['exact', 'mass_balance', 'estimated'] as const).map((certainty) => (
              <span key={certainty}>
                <svg width="26" height="8" aria-hidden="true">
                  <path d="M 0 4 L 26 4" className="ay-graph__legendline" {...(STROKE_DASH[certaintyStyle(certainty)] === undefined ? {} : { strokeDasharray: STROKE_DASH[certaintyStyle(certainty)] })} />
                </svg>
                <span className={`ay-graph__certainty ay-graph__certainty--${certaintyStyle(certainty)}`}>{certaintyLabel(certainty)}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SidePanel({
  selection,
  layout,
  traversal,
  summaries,
  labelOf,
  nameOf,
  onClose,
  onRecenter,
  onOpen,
  listRoute,
}: {
  selection: Selection;
  layout: Layout;
  traversal: WireTraversal;
  summaries: ReadonlyMap<string, ObjectSummary | null>;
  labelOf: (ref: WireRef) => string;
  nameOf: (ref: WireRef) => string;
  onClose: () => void;
  onRecenter: (ref: WireRef) => void;
  onOpen: (ref: WireRef) => void;
  listRoute: (type: string) => Route;
}) {
  const services = useAppServices();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), [selection]);
  if (selection === null) return null;
  if (selection.kind === 'group') {
    const group = layout.collapsed.get(selection.group);
    if (group === undefined) return null;
    const type = typeOfGroup(group.group);
    const members = group.members.map((member) => layout.placed.get(member)!).filter((node) => node !== undefined);
    return (
      <aside className="ay-card ay-graph__side" aria-labelledby="ay-graph-side-title">
        <div className="ay-graph__sidehead">
          <span className="ay-graph__eyebrow">Groupe replié</span>
          <h2 id="ay-graph-side-title" ref={heading} tabIndex={-1}>
            {members.length} {typeLabel(summaries.get(type), type, true).toLocaleLowerCase('fr')}
          </h2>
          <button type="button" className="ay-graph__close" aria-label="Fermer" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </div>
        <ul className="ay-graph__members" aria-label="Objets du groupe">
          {members.map((node) => (
            <li key={node.key}>
              <span>{labelOf(node.ref)}</span>
              <button type="button" className="ay-graph__linkbutton" onClick={() => onRecenter(node.ref)}>
                Recentrer
              </button>
              <button type="button" className="ay-graph__linkbutton" onClick={() => onOpen(node.ref)}>
                Ouvrir
              </button>
            </li>
          ))}
        </ul>
        <Btn variant="soft" onClick={() => services.navigate(listRoute(type))}>
          Voir la liste
        </Btn>
      </aside>
    );
  }
  const link = layout.links.find((candidate) => candidate.key === selection.key);
  if (link === undefined) return null;
  const edges = link.edges.map((index) => traversal.edges[index]!);
  const edge = edges[0]!;
  const direction = directionOf(layout.placed, edge);
  const far = farEnd(layout.placed, edge);
  const farType = typeLabel(summaries.get(far.type), far.type).toLocaleLowerCase('fr');
  const quantity = quantityOf(edge);
  const origin = edge.attributes?.['equivalence'] === 'same_as' ? 'Équivalence d’identité (tiers fusionné)' : edge.edge_id !== undefined ? 'Lien enregistré (commande ou import)' : 'Champ de référence de l’objet';
  return (
    <aside className="ay-card ay-graph__side" aria-labelledby="ay-graph-side-title">
      <div className="ay-graph__sidehead">
        <span className="ay-graph__eyebrow">Lien sélectionné</span>
        <h2 id="ay-graph-side-title" ref={heading} tabIndex={-1}>
          {relationLabel(summaries, edge)}
        </h2>
        <button type="button" className="ay-graph__close" aria-label="Fermer" onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <p className="ay-graph__pathline">
        {nameOf(edge.source)} <span aria-hidden="true">→</span> {nameOf(edge.target)}
      </p>
      <dl className="ay-graph__facts ay-graph__facts--panel">
        <dt>Sens</dt>
        <dd>{DIRECTION_LABELS[direction]}</dd>
        <dt>Certitude</dt>
        <dd>
          <span className={`ay-graph__certainty ay-graph__certainty--${certaintyStyle(edge.certainty)}`}>{certaintyLabel(edge.certainty)}</span>
          <br />
          <span className="ay-graph__muted">{CERTAINTY_HINTS[edge.certainty ?? 'exact'] ?? ''}</span>
        </dd>
        <dt>Date d’effet</dt>
        <dd>{traversal.source === 'authoritative' ? (traversal.at === undefined ? 'En vigueur à la lecture' : `En vigueur au ${dateTime(traversal.at)}`) : 'Non portée par la projection : voir le mode Traçabilité'}</dd>
        <dt>Origine</dt>
        <dd>{origin}</dd>
        {quantity !== null && (
          <>
            <dt>Quantité</dt>
            <dd>
              {quantity.value.toLocaleString('fr-FR')} {quantity.unit ?? ''}
            </dd>
          </>
        )}
      </dl>
      {edges.length > 1 && <p className="ay-graph__muted">{plural(edges.length, 'lien porté', 'liens portés')} par ce trait (groupe replié).</p>}
      <div className="ay-graph__actions">
        <Btn onClick={() => onRecenter(far)}>Recentrer</Btn>
        <Btn variant="dark" onClick={() => onOpen(far)}>
          Ouvrir {farType === '' ? 'la fiche' : `: ${farType}`}
        </Btn>
      </div>
      <LinkCorrection edge={edge} />
      <p className="ay-graph__muted">Une correction est approuvée par une seconde personne ; un lien ne devient jamais « exact » à la main.</p>
    </aside>
  );
}

function LinkTable({ traversal, layout, summaries, nameOf, onRecenter, onOpen }: { traversal: WireTraversal; layout: Layout; summaries: ReadonlyMap<string, ObjectSummary | null>; nameOf: (ref: WireRef) => string; onRecenter: (ref: WireRef) => void; onOpen: (ref: WireRef) => void }) {
  const rows = traversal.edges.map((edge, index) => ({ index, edge, direction: directionOf(layout.placed, edge), far: farEnd(layout.placed, edge) }));
  if (rows.length === 0) return <Empty label="Aucun lien lisible." />;
  return (
    <div className="ay-graph__tablewrap">
      <table className="ay-table ay-graph__table">
        <caption className="ay-visually-hidden">Liens de {nameOf(traversal.start)}</caption>
        <thead>
          <tr>
            <th scope="col">Relation</th>
            <th scope="col">Sens</th>
            <th scope="col">Objet</th>
            <th scope="col">Certitude</th>
            <th scope="col">Date d’effet</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.index}>
              <td>{relationLabel(summaries, row.edge)}</td>
              <td>{DIRECTION_LABELS[row.direction]}</td>
              <td>
                <button type="button" className="ay-graph__linkbutton" onClick={() => onRecenter(row.far)}>
                  {nameOf(row.far)}
                </button>{' '}
                <button type="button" className="ay-graph__linkbutton ay-graph__muted" aria-label={`Ouvrir la fiche ${nameOf(row.far)}`} onClick={() => onOpen(row.far)}>
                  ouvrir
                </button>
              </td>
              <td>
                <span className={`ay-graph__certainty ay-graph__certainty--${certaintyStyle(row.edge.certainty)}`}>{certaintyLabel(row.edge.certainty)}</span>
              </td>
              <td>{traversal.source === 'authoritative' ? (traversal.at === undefined ? 'à la lecture' : dateTime(traversal.at)) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
